/**
 * Descriptors dos elementos estáticos (divisor, imagem), do botão e dos tabulares
 * (tabela simples, tabela, grade de dados, planilha).
 *
 * A grade de dados é o componente mais usado das telas em produção e o mais rico:
 * é ela que carrega paginação, filtro, edição em linha, os quatro modos de
 * visualização e o mestre-detalhe.
 */
import { ScreenNode } from '../../model/node-props';
import { ComponentDescriptor, defineDescriptor } from '../component-descriptor';
import { buildLayoutGroup, buildStandardFieldGroups } from './common-groups';

/**
 * Onde um campo pode morar.
 *
 * `tab` entrou depois do relato "esta exigindo que sempre tenha um painel":
 * uma aba com um campo so nao precisa de painel nenhum, e obrigar a criar um
 * antes e a mesma friccao que fazia toda tela sair parecendo o mesmo cadastro.
 * O painel continua sendo o normal quando ha muitos campos.
 */
const FIELD_PARENTS = ['panel', 'row', 'section', 'tab'] as const;

/** Lê `viewMode` da grade sem espalhar cast pelo arquivo. */
function readGridViewMode(node: ScreenNode): string | undefined {
  return (node.props as { viewMode?: string }).viewMode;
}

/** Lê se o mestre-detalhe está ligado. */
function isMasterDetailEnabled(node: ScreenNode): boolean {
  const masterDetail = (node.props as { masterDetail?: { enabled?: boolean } }).masterDetail;
  return masterDetail?.enabled === true;
}

// ---------------------------------------------------------------- divisor

export const dividerDescriptor = defineDescriptor<'divider'>({
  type: 'divider',
  labelKey: 'builderScreen.palette.divider',
  descriptionKey: 'builderScreen.palette.divider.desc',
  icon: 'line',
  category: 'display',
  level: 'advanced',

  createDefault: context => ({
    props: { orientation: 'horizontal' },
    layout: { order: context.order, colSpan: 24 }
  }),

  allowedParents: [...FIELD_PARENTS],

  inspector: [
    {
      labelKey: 'builderScreen.inspector.groups.essential.title',
      level: 'simple',
      properties: [
        {
          kind: 'text',
          path: 'titleKey',
          labelKey: 'builderScreen.inspector.sectionTitle',
          level: 'simple'
        },
        {
          // Era um SIM/NAO ("tracejado?"), que so dava dois desenhos de linha.
          kind: 'enum',
          path: 'variant',
          labelKey: 'builderScreen.inspector.dividerVariant',
          helpKey: 'builderScreen.inspector.dividerVariant.help',
          level: 'simple',
          options: [
            { value: 'solid', labelKey: 'builderScreen.divider.solid' },
            { value: 'dashed', labelKey: 'builderScreen.divider.dashed' },
            { value: 'dotted', labelKey: 'builderScreen.divider.dotted' }
          ]
        },
        {
          // O PAPEL da cor. Sem hexadecimal: quem escolhe o tom e o tema.
          kind: 'enum',
          path: 'color',
          labelKey: 'builderScreen.inspector.dividerColor',
          helpKey: 'builderScreen.inspector.dividerColor.help',
          level: 'simple',
          options: [
            { value: 'default', labelKey: 'builderScreen.divider.color.default' },
            { value: 'muted', labelKey: 'builderScreen.divider.color.muted' },
            { value: 'strong', labelKey: 'builderScreen.divider.color.strong' },
            { value: 'accent', labelKey: 'builderScreen.divider.color.accent' },
            { value: 'success', labelKey: 'builderScreen.divider.color.success' },
            { value: 'warning', labelKey: 'builderScreen.divider.color.warning' },
            { value: 'danger', labelKey: 'builderScreen.divider.color.danger' }
          ]
        },
        {
          kind: 'enum',
          path: 'orientation',
          labelKey: 'builderScreen.inspector.dividerType',
          helpKey: 'builderScreen.inspector.dividerType.help',
          level: 'advanced',
          options: [
            // As duas usavam a chave do ROTULO do campo, entao a lista mostrava
            // "Tipo do divisor" duas vezes — o "esta repetido".
            { value: 'horizontal', labelKey: 'builderScreen.divider.horizontal' },
            { value: 'vertical', labelKey: 'builderScreen.divider.vertical' }
          ]
        },
        {
          kind: 'enum',
          path: 'titleAlign',
          labelKey: 'builderScreen.inspector.orientation',
          helpKey: 'builderScreen.inspector.orientation.help',
          level: 'advanced',
          options: [
            { value: 'left', labelKey: 'builderScreen.inspector.justify.start' },
            { value: 'center', labelKey: 'builderScreen.inspector.align.center' },
            { value: 'right', labelKey: 'builderScreen.inspector.justify.end' }
          ]
        }
      ]
    },
    buildLayoutGroup()
  ]
});

// ---------------------------------------------------------------- imagem

export const imageDescriptor = defineDescriptor<'image'>({
  type: 'image',
  labelKey: 'builderScreen.palette.image',
  descriptionKey: 'builderScreen.palette.image.desc',
  icon: 'picture',
  category: 'display',
  level: 'simple',

  createDefault: context => ({
    props: { enableLightbox: true },
    layout: { order: context.order, colSpan: 6 }
  }),

  allowedParents: [...FIELD_PARENTS],

  inspector: [
    {
      labelKey: 'builderScreen.inspector.imageSection',
      level: 'simple',
      properties: [
        {
          kind: 'text',
          path: 'url',
          labelKey: 'builderScreen.inspector.imageUrl',
          helpKey: 'builderScreen.inspector.imageUrl.help',
          placeholderKey: 'builderScreen.inspector.imageUrl.placeholder',
          level: 'simple'
        },
        {
          kind: 'dataPath',
          path: 'dataPath',
          target: 'binding',
          labelKey: 'builderScreen.inspector.dataPath',
          placeholderKey: 'builderScreen.inspector.placeholder.image',
          level: 'simple'
        },
        {
          kind: 'text',
          path: 'altKey',
          labelKey: 'builderScreen.inspector.alt',
          helpKey: 'builderScreen.inspector.alt.help',
          level: 'simple'
        },
        {
          kind: 'boolean',
          path: 'enableLightbox',
          labelKey: 'builderScreen.inspector.enableLightbox',
          helpKey: 'builderScreen.inspector.enableLightbox.help',
          level: 'simple'
        },
        {
          kind: 'number',
          path: 'width',
          labelKey: 'builderScreen.inspector.width',
          level: 'advanced',
          min: 0
        },
        {
          kind: 'number',
          path: 'height',
          labelKey: 'builderScreen.inspector.height',
          level: 'advanced',
          min: 0
        }
      ]
    },
    buildLayoutGroup()
  ]
});

// ---------------------------------------------------------------- botão

export const buttonDescriptor = defineDescriptor<'button'>({
  type: 'button',
  labelKey: 'builderScreen.palette.button',
  descriptionKey: 'builderScreen.palette.button.desc',
  icon: 'plus-square',
  category: 'action',
  level: 'simple',

  createDefault: context => ({
    props: { labelKey: 'builderScreen.button.defaultText', variant: 'default', size: 'default' },
    layout: { order: context.order, colSpan: 6 }
  }),

  allowedParents: [...FIELD_PARENTS],

  inspector: [
    {
      labelKey: 'builderScreen.inspector.groups.essential.title',
      level: 'simple',
      properties: [
        {
          kind: 'text',
          path: 'labelKey',
          labelKey: 'builderScreen.button.properties.text',
          level: 'simple'
        },
        {
          kind: 'custom',
          path: 'icon',
          editor: 'iconPicker',
          labelKey: 'builderScreen.button.properties.icon',
          level: 'simple'
        },
        {
          kind: 'enum',
          path: 'variant',
          labelKey: 'builderScreen.button.properties.buttonType',
          level: 'simple',
          options: [
            { value: 'default', labelKey: 'builderScreen.common.default' },
            { value: 'primary', labelKey: 'builderScreen.inspector.buttonType.primary' },
            { value: 'dashed', labelKey: 'builderScreen.inspector.buttonType.dashed' },
            { value: 'text', labelKey: 'builderScreen.common.text' },
            { value: 'link', labelKey: 'builderScreen.inspector.buttonType.link' },
            { value: 'danger', labelKey: 'builderScreen.inspector.buttonType.danger' }
          ]
        },
        {
          kind: 'enum',
          path: 'size',
          labelKey: 'builderScreen.button.properties.size',
          level: 'advanced',
          options: [
            { value: 'small', labelKey: 'builderScreen.common.size.small' },
            { value: 'default', labelKey: 'builderScreen.common.default' },
            { value: 'large', labelKey: 'builderScreen.common.size.large' }
          ]
        },
        {
          kind: 'boolean',
          path: 'disabled',
          labelKey: 'builderScreen.button.properties.disabled',
          level: 'advanced'
        }
      ]
    },
    {
      labelKey: 'builderScreen.inspector.flowSection',
      level: 'simple',
      properties: [
        {
          kind: 'enum',
          path: 'action',
          labelKey: 'builderScreen.button.properties.actionType',
          level: 'simple',
          clearable: true,
          options: [
            { value: 'submit', labelKey: 'builderScreen.button.actionType.save' },
            { value: 'cancel', labelKey: 'builderScreen.button.actionType.cancel' },
            { value: 'callFlow', labelKey: 'builderScreen.inspector.action.runFlow' },
            { value: 'custom', labelKey: 'builderScreen.button.actionType.custom' }
          ]
        },
        {
          kind: 'custom',
          path: 'flow',
          editor: 'flowConfig',
          labelKey: 'builderScreen.flowConfig.title',
          helpKey: 'builderScreen.flowConfig.help',
          level: 'simple',
          // Só faz sentido quando a ação é executar fluxo.
          visibleWhen: (node: ScreenNode) =>
            (node.props as { action?: string }).action === 'callFlow'
        },
        {
          kind: 'enum',
          path: 'flow.dataMode',
          labelKey: 'builderScreen.inspector.button.flowDataMode',
          helpKey: 'builderScreen.inspector.button.flowDataMode.help',
          level: 'advanced',
          options: [
            { value: 'full', labelKey: 'builderScreen.inspector.button.flowDataMode.full' },
            { value: 'selectedItem', labelKey: 'builderScreen.inspector.button.flowDataMode.selectedItem' }
          ],
          visibleWhen: (node: ScreenNode) =>
            (node.props as { action?: string }).action === 'callFlow'
        }
      ]
    },
    buildLayoutGroup()
  ]
});

// ---------------------------------------------------------------- tabulares

export const simpleTableDescriptor = defineDescriptor<'simpletable'>({
  type: 'simpletable',
  labelKey: 'builderScreen.palette.simpletable',
  descriptionKey: 'builderScreen.palette.simpletable.desc',
  icon: 'table',
  category: 'data',
  level: 'advanced',

  createDefault: context => ({
    props: { columns: [] },
    layout: { order: context.order, colSpan: 24 }
  }),

  allowedParents: [...FIELD_PARENTS],

  inspector: buildStandardFieldGroups([
    {
      kind: 'custom',
      path: 'columns',
      editor: 'columnManager',
      labelKey: 'builderScreen.inspector.columnConfiguration',
      level: 'advanced'
    }
  ]),

  // `array` porque o widget faz `[formArrayName]`. Ver `FormContribution`.
  form: { defaultValue: () => [], control: 'array' }
});

export const tableDescriptor = defineDescriptor<'table'>({
  type: 'table',
  labelKey: 'builderScreen.palette.table',
  descriptionKey: 'builderScreen.palette.table.desc',
  icon: 'table',
  category: 'data',
  level: 'advanced',

  createDefault: context => ({
    props: { columns: [], sortable: true, filterable: false },
    layout: { order: context.order, colSpan: 24 }
  }),

  allowedParents: [...FIELD_PARENTS],

  inspector: buildStandardFieldGroups([
    {
      kind: 'custom',
      path: 'columns',
      editor: 'columnManager',
      labelKey: 'builderScreen.inspector.columnConfiguration',
      level: 'advanced'
    },
    {
      kind: 'boolean',
      path: 'sortable',
      labelKey: 'builderScreen.inspector.sorting',
      helpKey: 'builderScreen.inspector.sorting.help',
      level: 'advanced'
    },
    {
      kind: 'boolean',
      path: 'filterable',
      labelKey: 'builderScreen.inspector.filtering',
      helpKey: 'builderScreen.inspector.filtering.help',
      level: 'advanced'
    }
  ]),

  // `array` porque o widget faz `[formArrayName]`. Ver `FormContribution`.
  form: { defaultValue: () => [], control: 'array' }
});

export const dataGridDescriptor = defineDescriptor<'datagrid'>({
  type: 'datagrid',
  labelKey: 'builderScreen.palette.datagrid',
  descriptionKey: 'builderScreen.palette.datagrid.desc',
  icon: 'table',
  category: 'data',
  level: 'simple',

  createDefault: context => ({
    props: {
      columns: [],
      paginated: true,
      pageSize: 10,
      sortable: true,
      filterable: true,
      enableCrud: true,
      selectionMode: 'single',
      viewMode: 'inline'
    },
    layout: { order: context.order, colSpan: 24, rowSpan: 3 }
  }),

  allowedParents: [...FIELD_PARENTS],

  inspector: [
    {
      labelKey: 'builderScreen.inspector.groups.essential.title',
      level: 'simple',
      properties: [
        {
          kind: 'text',
          path: 'labelKey',
          labelKey: 'builderScreen.datagrid.properties.label',
          helpKey: 'builderScreen.datagrid.properties.label.help',
          placeholderKey: 'builderScreen.datagrid.properties.label.placeholder',
          level: 'simple'
        },
        {
          kind: 'dataPath',
          path: 'dataPath',
          target: 'binding',
          labelKey: 'builderScreen.inspector.dataPath',
          helpKey: 'builderScreen.inspector.gridDataPathHelp',
          level: 'simple'
        },
        {
          kind: 'custom',
          path: 'columns',
          editor: 'columnManager',
          labelKey: 'builderScreen.inspector.columnConfiguration',
          helpKey: 'builderScreen.inspector.columnsHelp',
          level: 'simple'
        }
      ]
    },
    {
      labelKey: 'builderScreen.generator.gridFeatures',
      level: 'simple',
      properties: [
        {
          kind: 'boolean',
          path: 'enableCrud',
          labelKey: 'builderScreen.inspector.dataGrid.enableCRUD',
          level: 'simple'
        },
        {
          kind: 'boolean',
          path: 'paginated',
          labelKey: 'builderScreen.inspector.pagination',
          helpKey: 'builderScreen.inspector.pagination.help',
          level: 'simple'
        },
        {
          kind: 'number',
          path: 'pageSize',
          labelKey: 'builderScreen.generator.itemsPerPage',
          level: 'simple',
          min: 1,
          max: 200,
          visibleWhen: (node: ScreenNode) =>
            (node.props as { paginated?: boolean }).paginated === true
        },
        {
          kind: 'boolean',
          path: 'sortable',
          labelKey: 'builderScreen.inspector.sorting',
          level: 'advanced'
        },
        {
          kind: 'boolean',
          path: 'filterable',
          labelKey: 'builderScreen.inspector.filtering',
          level: 'advanced'
        },
        {
          kind: 'number',
          path: 'maxVisibleRows',
          labelKey: 'builderScreen.inspector.maxRowsHelp',
          helpKey: 'builderScreen.inspector.maxRowsHelp',
          level: 'advanced',
          min: 0
        },
        {
          kind: 'enum',
          path: 'selectionMode',
          labelKey: 'builderScreen.inspector.selection.single',
          level: 'advanced',
          options: [
            { value: 'none', labelKey: 'builderScreen.common.none' },
            { value: 'single', labelKey: 'builderScreen.inspector.selection.single' },
            { value: 'multiple', labelKey: 'builderScreen.inspector.selection.multiple' }
          ]
        }
      ]
    },
    {
      labelKey: 'builderScreen.inspector.masterDetail',
      level: 'advanced',
      collapsed: true,
      properties: [
        {
          kind: 'boolean',
          path: 'masterDetail.enabled',
          labelKey: 'builderScreen.inspector.masterDetail',
          helpKey: 'builderScreen.inspector.masterDetailHelp',
          level: 'advanced'
        },
        {
          kind: 'custom',
          path: 'masterDetail.panelIds',
          editor: 'panelPicker',
          labelKey: 'builderScreen.inspector.selectPanels',
          helpKey: 'builderScreen.inspector.multiPanelHelp',
          level: 'advanced',
          visibleWhen: isMasterDetailEnabled
        },
        {
          kind: 'boolean',
          path: 'masterDetail.autoGenerateFields',
          labelKey: 'builderScreen.inspector.generateFieldsHelp',
          level: 'advanced',
          visibleWhen: isMasterDetailEnabled
        }
      ]
    },
    {
      labelKey: 'builderScreen.common.advanced',
      level: 'advanced',
      collapsed: true,
      properties: [
        {
          kind: 'enum',
          path: 'viewMode',
          labelKey: 'builderScreen.inspector.viewModeHelp',
          helpKey: 'builderScreen.inspector.viewModeHelp',
          level: 'advanced',
          options: [
            { value: 'inline', labelKey: 'builderScreen.inspector.viewMode.inline' },
            { value: 'flip', labelKey: 'builderScreen.inspector.viewMode.flip' },
            { value: 'drawer', labelKey: 'builderScreen.inspector.viewMode.drawer' },
            { value: 'spreadsheet', labelKey: 'builderScreen.inspector.viewMode.spreadsheet' }
          ]
        },
        {
          kind: 'number',
          path: 'drawerWidth',
          labelKey: 'builderScreen.inspector.drawerWidthHelp',
          helpKey: 'builderScreen.inspector.drawerWidthHelp',
          level: 'advanced',
          min: 10,
          max: 90,
          // Só aparece no modo gaveta — no antigo ficava sempre visível e confundia.
          visibleWhen: (node: ScreenNode) => readGridViewMode(node) === 'drawer'
        },
        {
          kind: 'boolean',
          path: 'autoAddFirstRow',
          labelKey: 'builderScreen.inspector.autoAddSection',
          helpKey: 'builderScreen.inspector.autoAddHelp',
          level: 'advanced'
        },
        {
          kind: 'number',
          path: 'autoAddOrder',
          labelKey: 'builderScreen.inspector.autoAddOrderHelp',
          helpKey: 'builderScreen.inspector.autoAddOrderHelp',
          level: 'advanced',
          min: 0,
          visibleWhen: (node: ScreenNode) =>
            (node.props as { autoAddFirstRow?: boolean }).autoAddFirstRow === true
        }
      ]
    },
    buildLayoutGroup()
  ],

  form: { defaultValue: () => [] }
});

/**
 * Planilha. Mantida no catálogo porque o componente está vivo no canvas antigo e
 * o tipo é oferecido na paleta — nenhuma das telas de produção examinadas usa,
 * mas a amostra é pequena demais para justificar a remoção.
 */
export const spreadsheetDescriptor = defineDescriptor<'spreadsheet'>({
  type: 'spreadsheet',
  labelKey: 'builderScreen.palette.spreadsheet',
  descriptionKey: 'builderScreen.palette.spreadsheet.desc',
  icon: 'table',
  category: 'data',
  level: 'advanced',

  createDefault: context => ({
    props: { showToolbar: true, showFormulaBar: false, readonly: false },
    layout: { order: context.order, colSpan: 24, rowSpan: 6 }
  }),

  allowedParents: [...FIELD_PARENTS],

  inspector: [
    {
      labelKey: 'builderScreen.inspector.groups.essential.title',
      level: 'advanced',
      properties: [
        {
          kind: 'text',
          path: 'labelKey',
          labelKey: 'builderScreen.inspector.label',
          level: 'advanced'
        },
        {
          kind: 'boolean',
          path: 'showToolbar',
          labelKey: 'builderScreen.inspector.preview',
          level: 'advanced'
        },
        {
          kind: 'boolean',
          path: 'readonly',
          labelKey: 'builderScreen.inspector.readonly',
          level: 'advanced'
        }
      ]
    },
    buildLayoutGroup()
  ],

  form: { defaultValue: () => null }
});

export const DISPLAY_AND_DATA_DESCRIPTORS: readonly ComponentDescriptor[] = [
  dividerDescriptor,
  imageDescriptor,
  buttonDescriptor,
  simpleTableDescriptor,
  tableDescriptor,
  dataGridDescriptor,
  spreadsheetDescriptor
];
